import React, { useEffect, useState } from "react";
import { getStockSentiment } from "../lib/api";
import { Activity, Gauge, AlertCircle, ThumbsUp, ThumbsDown, TrendingUp, TrendingDown } from "lucide-react";

const COLOR_BY_SCORECARD = {
  green: "text-[var(--positive,#10B981)] bg-[var(--positive,#10B981)]/10 border-[var(--positive,#10B981)]/25",
  red: "text-[var(--negative,#EF4444)] bg-[var(--negative,#EF4444)]/10 border-[var(--negative,#EF4444)]/25",
  yellow: "text-[var(--warning,#F59E0B)] bg-[var(--warning,#F59E0B)]/10 border-[var(--warning,#F59E0B)]/25",
};

const MMI_ZONE = {
  extreme_fear: { label: "Extreme Fear", color: "text-red-400", bg: "bg-red-500/10" },
  fear: { label: "Fear", color: "text-orange-400", bg: "bg-orange-500/10" },
  neutral: { label: "Neutral", color: "text-amber-400", bg: "bg-amber-500/10" },
  greed: { label: "Greed", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  extreme_greed: { label: "Extreme Greed", color: "text-emerald-300", bg: "bg-emerald-500/20" },
  unknown: { label: "—", color: "text-[var(--text-muted)]", bg: "bg-white/5" },
};

export default function SentimentPanel({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    getStockSentiment(symbol)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load sentiment"))
      .finally(() => setLoading(false));
  }, [symbol]);

  if (loading) {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5 animate-pulse" data-testid="sentiment-panel-loading">
        <div className="h-4 w-40 bg-white/5 rounded mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-20 bg-white/5 rounded" />
          ))}
        </div>
      </div>
    );
  }
  if (error || !data) {
    return null;  // Silently skip if sentiment unavailable
  }

  const sections = data.scorecard?.sections || {};
  const news = data.news_sentiment || {};
  const mmi = data.mmi || {};
  const info = data.info || {};
  const ratios = info.ratios || {};
  const mmiZone = MMI_ZONE[mmi.zone] || MMI_ZONE.unknown;

  const scoreOrder = ["performance", "valuation", "growth", "profitability", "entry_point", "red_flags"];
  const visible = scoreOrder.filter((k) => sections[k]);

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5 space-y-5" data-testid="sentiment-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[var(--brand)]" />
          <h3 className="text-sm font-semibold text-white tracking-wide" style={{ fontFamily: "Chivo" }}>
            Sentiment & Scorecard
          </h3>
          <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">via Tickertape</span>
        </div>
        <div className="flex items-center gap-3">
          {/* News sentiment pill */}
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${news.label === "positive" ? COLOR_BY_SCORECARD.green : news.label === "negative" ? COLOR_BY_SCORECARD.red : "text-[var(--text-muted)] bg-white/5 border-white/10"}`} data-testid="news-sentiment">
            {news.label === "positive" && <ThumbsUp size={12} />}
            {news.label === "negative" && <ThumbsDown size={12} />}
            News: <b>{news.label || "neutral"}</b>
            <span className="text-[10px] opacity-70">({news.total || 0} articles)</span>
          </div>
          {/* MMI pill */}
          {mmi.indicator !== null && mmi.indicator !== undefined && (
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 text-xs ${mmiZone.bg}`} data-testid="mmi-pill">
              <Gauge size={12} className={mmiZone.color} />
              <span className="text-[var(--text-muted)]">Market:</span>
              <b className={mmiZone.color}>{mmiZone.label}</b>
              <span className="text-[10px] text-[var(--text-muted)]">({Math.round(mmi.indicator)})</span>
            </div>
          )}
        </div>
      </div>

      {/* Scorecard grid */}
      {visible.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="scorecard-grid">
          {visible.map((key) => {
            const sec = sections[key];
            const colorCls = COLOR_BY_SCORECARD[sec.colour] || "text-[var(--text-muted)] bg-white/5 border-white/10";
            return (
              <div key={key} className={`rounded-md border ${colorCls} p-3`} data-testid={`score-${key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider opacity-80">{sec.name}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/10">{sec.tag || "—"}</span>
                </div>
                <div className="text-xs mt-1.5 leading-snug opacity-90">{sec.description}</div>
                {sec.elements?.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {sec.elements.slice(0, 3).map((el, i) => (
                      <li key={i} className="text-[10px] opacity-75 flex items-start gap-1">
                        <span className="mt-0.5">•</span>
                        <span><b>{el.title}:</b> {el.description}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Quick ratios */}
      {Object.keys(ratios).length > 0 && (
        <div className="pt-3 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="sentiment-ratios">
          {ratios["52w_high"] && (
            <Metric label="52w High" value={`₹${Math.round(ratios["52w_high"])}`} />
          )}
          {ratios["52w_low"] && (
            <Metric label="52w Low" value={`₹${Math.round(ratios["52w_low"])}`} />
          )}
          {ratios["52w_pct"] !== undefined && ratios["52w_pct"] !== null && (
            <Metric
              label="52w Return"
              value={`${ratios["52w_pct"] >= 0 ? "+" : ""}${ratios["52w_pct"].toFixed(1)}%`}
              icon={ratios["52w_pct"] >= 0 ? TrendingUp : TrendingDown}
              tone={ratios["52w_pct"] >= 0 ? "positive" : "negative"}
            />
          )}
          {ratios.beta !== undefined && ratios.beta !== null && (
            <Metric label="Beta" value={ratios.beta.toFixed(2)} />
          )}
        </div>
      )}

      {/* Tags / Themes */}
      {info.tags && info.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-white/10">
          <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mr-1">Themes</span>
          {info.tags.slice(0, 8).map((t, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[var(--text-secondary)] border border-white/10">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, icon: Icon, tone }) {
  const toneCls = tone === "positive" ? "text-[var(--positive,#10B981)]" : tone === "negative" ? "text-[var(--negative,#EF4444)]" : "text-white";
  return (
    <div>
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-sm ${toneCls} flex items-center gap-1`}>
        {Icon && <Icon size={12} />}
        {value}
      </div>
    </div>
  );
}
